<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { scrollToError, showSnackbar } from '@/helper'
import { useRoute, useRouter } from "vue-router"
import { usePayment } from "@/composables/usePayment"
import { useCheckout } from "@/app/Checkout/composables/useCheckout"
import { useAddresses } from "@/app/Checkout/composables/useAddresses"
import AddressForm from "@/app/Checkout/components/common/AddressForm/index.vue"
import NBConfirmModal from "@/app/Checkout/components/common/NBConfirmModal/index.vue"
import { useAddressValidation } from "@/app/Checkout/composables/useAddressValidation"
import { useAuth } from "@/app/Checkout/composables/useAuth"
import { useUser } from "@/app/Checkout/composables/useUser"

const route = useRoute()
const formRef = ref(null)
const router = useRouter()
const isDirty = ref(false)
const isComplete = ref(false)
const addressError = ref('')
const showDeleteConfirm = ref(false)
const { getPaymentMethods } = usePayment()
const { checkout, getCheckout, setShippingAddress, setInvoiceAddress } = useCheckout()
const isEdit = ref(Boolean(route.params?.id))
const addressType = ref(route?.query?.type)
const { emptyAddress, emptyLoggedInNewAddress } = useAddressValidation()
const { isLoggedIn } = useAuth()
const { user } = useUser()
const { errors, pending, setErrors, resetErrors, resetAllErrors, getAddresses, createAddress, updateAddress, deleteAddress, getAddressFromId, clearFieldError, enrichedAddresses, isBillingAddressSame } = useAddresses()

const currentAddress = ref(emptyAddress())
const canSave = computed(() => {
  if (!isComplete.value) return false
  return isEdit.value ? isDirty.value : true
})

const clearErrorField = (field) => {
  clearFieldError({ field, type: addressType.value })
}

onMounted(async () => {
  resetErrors('delivery_address')
  resetErrors('invoice_address')

  await nextTick()
  const address = currentAddress.value || {}
  const isFilled = Boolean(address.gender || address.firstname || address.lastname)
  if (isFilled && (address.is_invalid || !isComplete.value) ) {
    await saveAddress()
  }
})

if (isEdit.value) {
  currentAddress.value = getAddressFromId(route.params?.id)
  currentAddress.value.phone = currentAddress.value.phone_with_country_code?.phone || currentAddress.value.phone || ''
  currentAddress.value.phone_country_code = currentAddress.value.phone_with_country_code?.country || currentAddress.value.country || 'DE'
} else if (route?.query?.type) {
  currentAddress.value = isLoggedIn.value
    ? emptyLoggedInNewAddress()
    : checkout.value[route?.query?.type]
}

// Logged-in prefill depends on async user profile loading.
// If `user` arrives after this screen mounts, we need to re-apply defaults.
watch(
  () => user.value,
  () => {
    if (isEdit.value) return
    if (!route?.query?.type) return
    if (!isLoggedIn.value) return
    if (isDirty.value) return // don't overwrite user input once they start editing

    currentAddress.value = emptyLoggedInNewAddress()
  }
)

const saveAddress = async () => {

  const payload = formRef.value?.formData()
  const errorType = addressType.value || 'delivery_address'
  const isInvoiceAddress = errorType === 'invoice_address'
  resetErrors(errorType)

  try {
    let response
    const extendedPayload = {
      ...payload,
      is_invoice: isInvoiceAddress,
      is_delivery: !isInvoiceAddress,
      is_same: isBillingAddressSame.value,
    }
    if (isEdit.value) {
      response = await updateAddress({ id: route.params?.id, data: extendedPayload })
    } else {
      response = await createAddress(extendedPayload)
    }

    if (response?.redirect && response?.url) {
      window.location.href = response.url
    } else {
      const savedAddressId = response?.id || route.params?.id

      if (savedAddressId) {
        if (errorType === 'delivery_address') {
          await setShippingAddress(savedAddressId, isBillingAddressSame.value)
        } else if (errorType === 'invoice_address') {
          await setInvoiceAddress(savedAddressId)
        }
      }

      await getPaymentMethods()
      await getCheckout()
      await goBack()
    }
  } catch (err) {
    const hasFieldErrors = err?.errors && typeof err.errors === 'object' && Object.keys(err.errors).length > 0
    const shouldRedirect = err?.redirect && err?.url && !hasFieldErrors
    if (shouldRedirect) {
      window.location.href = err.url
      return
    }
    setErrors({ errors: err?.errors ?? err, type: errorType })
    scrollToError()
  }
}

const goBack = async () => {
  resetAllErrors()
  await getAddresses()
  window.history.back()
}

async function confirmDeleteAddress() {
  showDeleteConfirm.value = false

  await deleteAddress(route.params?.id)
  await getAddresses()

  router.push({ name: 'AddressList' })
}
</script>
